import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCities,
  deleteCity,
} from '../../store/slices/citiesSlice';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmDialog from '../common/ConfirmDialog';
import AddEditCityDialog from './AddEditCityDialog';

const CitiesTab = () => {
  const dispatch = useDispatch();
  const cities = useSelector(state => state.cities?.items || []);
  const loading = useSelector(state => state.cities?.loading || false);
  const { user: currentUser } = useSelector(state => state.auth);
  const [dialogMode, setDialogMode] = useState({ type: null, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, item: null });
  const [searchTerm, setSearchTerm] = useState('');

  const userRole = currentUser?.profile?.role;
  const hasEditPermission = ['super_admin', 'admin'].includes(userRole);

  useEffect(() => {
    dispatch(fetchCities());
  }, [dispatch]);

  const handleAdd = () => {
    if (!hasEditPermission) return;
    setDialogMode({ type: 'add', data: null });
  };

  const handleEdit = (city) => {
    if (!hasEditPermission) return;
    setDialogMode({ type: 'edit', data: city });
  };

  const handleDelete = (city) => {
    if (!hasEditPermission) return;
    setDeleteConfirm({ show: true, item: city });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.item) {
      await dispatch(deleteCity(deleteConfirm.item._id));
      setDeleteConfirm({ show: false, item: null });
      dispatch(fetchCities());
    }
  };

  const filteredCities = cities.filter(city => 
    city.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    city.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    city.state.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-medium">Cities ({filteredCities.length})</h3>
        {hasEditPermission && (
          <button 
            onClick={handleAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add City
          </button>
        )}
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search cities..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="border rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCities.map((city) => (
                  <tr key={city._id}>
                    <td className="px-6 py-4 whitespace-nowrap">{city.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{city.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{city.state}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {hasEditPermission ? (
                        <div className="flex space-x-4">
                          <button
                            onClick={() => handleEdit(city)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(city)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No actions available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {hasEditPermission && (
        <>
          <AddEditCityDialog
            isOpen={!!dialogMode.type}
            mode={dialogMode.type}
            data={dialogMode.data}
            onClose={() => setDialogMode({ type: null, data: null })}
            onSuccess={() => {
              setDialogMode({ type: null, data: null });
              dispatch(fetchCities());
            }}
          />

          <ConfirmDialog
            isOpen={deleteConfirm.show}
            onClose={() => setDeleteConfirm({ show: false, item: null })}
            onConfirm={handleConfirmDelete}
            title="Delete City"
            message={`Are you sure you want to delete "${deleteConfirm.item?.name}"? This action cannot be undone.`}
          />
        </>
      )}
    </div>
  );
};

export default CitiesTab; 